/**
 * Short Link Hooks
 *
 * React Query hooks for arthaus.link short links.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { shortLinksApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { CreateShortLinkData } from '../types'

export const shortLinkKeys = {
  all: ['short-links'] as const,
  list: (filters: Record<string, unknown>) => [...shortLinkKeys.all, 'list', filters] as const,
}

export function useShortLinks(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: shortLinkKeys.list(params || {}),
    queryFn: () => shortLinksApi.list(params),
  })
}

export function useCreateShortLink() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateShortLinkData) => shortLinksApi.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: shortLinkKeys.all })

      // The row is saved either way; what differs is whether it resolves yet.
      // Saying "created" when the edge push failed would send an artist off to
      // share a link that 404s, so the two cases get different words.
      if (result.synced) {
        toast({
          title: 'Link created',
          description: `${result.data.shortUrl} is live.`,
        })
      } else {
        toast({
          title: 'Link saved, not live yet',
          description:
            'It is stored safely but has not reached the link network. It will start working once it syncs.',
          variant: 'destructive',
        })
      }
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not create that link.'
      toast({ title: 'Link not created', description: message, variant: 'destructive' })
    },
  })
}

export function useSetShortLinkActive() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      shortLinksApi.setActive(id, active),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: shortLinkKeys.all })
      toast({
        title: variables.active ? 'Link restored' : 'Link retired',
        description: variables.active
          ? `${result.data.shortUrl} is working again.`
          : `${result.data.shortUrl} no longer resolves. Anywhere it is already printed will stop working.`,
      })
    },
  })
}
